/**
 * AvaliarAlternativaDialog - Modal de avaliação de sugestão alternativa enviada por um fornecedor.
 *
 * 3 opções:
 *   A) Vincular a produto existente (busca por nome/SKU)
 *   B) Cadastrar novo produto (formulário simplificado inline)
 *   C) Adotar como texto livre (sem vínculo a produto)
 *
 * Props:
 *  - avaliar: { itemIdx, item, fornecedor, info } | null
 *  - cotacaoId: string
 *  - onClose: () => void
 *  - onAdotado: () => void  (callback chamado após sucesso para refetch)
 */
import React, { useState, useEffect, useMemo } from 'react';
import api from '../../lib/axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Link2, Plus, FileText, Search } from 'lucide-react';
import { toast } from 'sonner';

const AvaliarAlternativaDialog = ({ avaliar, cotacaoId, onClose, onAdotado }) => {
  const [opcao, setOpcao] = useState('vincular'); // 'vincular' | 'cadastrar' | 'texto_livre'
  const [loading, setLoading] = useState(false);
  const [descricaoFinal, setDescricaoFinal] = useState('');

  // Opção A — busca de produto
  const [searchTerm, setSearchTerm] = useState('');
  const [produtos, setProdutos] = useState([]);
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [searching, setSearching] = useState(false);

  // Opção B — novo produto
  const [novoProduto, setNovoProduto] = useState({
    nome: '', sku: '', categoria: '', marca: '', modelo: '', unidade: 'UN',
    preco_custo: 0, preco_venda: 0,
  });
  const [categoriasLookup, setCategoriasLookup] = useState([]);

  // Reset state quando abre
  useEffect(() => {
    if (avaliar) {
      const sug = avaliar.info?.descricao_alternativa || '';
      setDescricaoFinal(sug);
      setSearchTerm(sug);
      setNovoProduto({
        nome: sug,
        sku: '',
        categoria: '',
        marca: avaliar.info?.marca_alternativa || '',
        modelo: '',
        unidade: avaliar.item?.unidade || 'UN',
        preco_custo: avaliar.info?.preco_unitario || 0,
        preco_venda: 0,
      });
      setProdutoSelecionado(null);
      setProdutos([]);
      setOpcao('vincular');
    }
  }, [avaliar]);

  // Carrega categorias para o select
  useEffect(() => {
    if (!avaliar) return;
    (async () => {
      try {
        const r = await api.get('/produtos?limit=1000');
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        const cats = Array.from(new Set(list.map(p => p.categoria).filter(Boolean))).sort();
        setCategoriasLookup(cats);
      } catch {}
    })();
  }, [avaliar]);

  // Busca produtos quando digita no search
  useEffect(() => {
    if (opcao !== 'vincular' || !searchTerm || searchTerm.length < 2) {
      setProdutos([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get(`/produtos?search=${encodeURIComponent(searchTerm)}&limit=20`);
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setProdutos(list);
      } catch (e) {
        setProdutos([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm, opcao]);

  if (!avaliar) return null;
  const { itemIdx, item, fornecedor, info } = avaliar;

  const handleConfirmar = async () => {
    if (!descricaoFinal?.trim()) {
      toast.error('A descrição final é obrigatória');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        fornecedor_id: fornecedor.fornecedor_id,
        opcao,
        descricao_final: descricaoFinal.trim(),
      };
      if (opcao === 'vincular') {
        if (!produtoSelecionado) {
          toast.error('Selecione um produto da lista');
          setLoading(false);
          return;
        }
        payload.produto_id = produtoSelecionado.id;
      } else if (opcao === 'cadastrar') {
        if (!novoProduto.nome?.trim()) {
          toast.error('Nome do novo produto é obrigatório');
          setLoading(false);
          return;
        }
        payload.novo_produto = novoProduto;
      }
      await api.put(`/cotacoes/${cotacaoId}/itens/${itemIdx}/adotar-alternativa`, payload);
      toast.success('Sugestão adotada — item atualizado na cotação');
      if (onAdotado) onAdotado();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao adotar alternativa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!avaliar} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="avaliar-alternativa-dialog"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-base">Avaliar sugestão de alternativa</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Resumo da sugestão */}
          <div className="p-3 rounded-lg bg-orange-50/70 border border-orange-200 space-y-1 text-sm" data-testid="resumo-sugestao">
            <div className="text-[11px] text-orange-700 font-semibold">Sugestão enviada por {fornecedor.fornecedor_nome}</div>
            <div>
              <span className="text-slate-500">Item original:</span>{' '}
              <span className="font-medium">{item.descricao}</span>
            </div>
            <div>
              <span className="text-slate-500">Alternativa:</span>{' '}
              <span className="font-semibold text-orange-700">{info.descricao_alternativa}</span>
              {info.marca_alternativa && <span className="text-slate-500"> · marca: {info.marca_alternativa}</span>}
            </div>
            {info.motivo_alternativa && (
              <div className="text-[12px] italic text-slate-600">Motivo: {info.motivo_alternativa}</div>
            )}
            <div className="text-[12px] text-slate-500">
              Preço cotado: <span className="font-semibold text-slate-700">R$ {(info.preco_unitario || 0).toFixed(2).replace('.', ',')}</span>
              {' '}· Qtd: {item.quantidade} {item.unidade || 'UN'}
            </div>
          </div>

          {/* Descrição final (editável) */}
          <div>
            <Label className="text-xs">Descrição que será aplicada ao item da cotação <span className="text-red-500">*</span></Label>
            <Input
              value={descricaoFinal}
              onChange={e => setDescricaoFinal(e.target.value)}
              placeholder="Ex: Bateria Heliar 105Ah Free 24M"
              data-testid="input-descricao-final"
            />
          </div>

          {/* 3 opções */}
          <div>
            <Label className="text-xs mb-2 block">Como deseja registrar este produto?</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setOpcao('vincular')}
                className={`p-3 rounded-lg border text-left transition-all ${opcao === 'vincular' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}
                data-testid="opcao-vincular"
              >
                <Link2 className="w-4 h-4 mb-1 text-blue-600" />
                <div className="text-xs font-semibold">Vincular existente</div>
                <div className="text-[10px] text-slate-500">Buscar no cadastro atual</div>
              </button>
              <button
                type="button"
                onClick={() => setOpcao('cadastrar')}
                className={`p-3 rounded-lg border text-left transition-all ${opcao === 'cadastrar' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}
                data-testid="opcao-cadastrar"
              >
                <Plus className="w-4 h-4 mb-1 text-emerald-600" />
                <div className="text-xs font-semibold">Cadastrar novo</div>
                <div className="text-[10px] text-slate-500">Criar produto na base</div>
              </button>
              <button
                type="button"
                onClick={() => setOpcao('texto_livre')}
                className={`p-3 rounded-lg border text-left transition-all ${opcao === 'texto_livre' ? 'border-slate-500 bg-slate-100' : 'border-slate-200 hover:bg-slate-50'}`}
                data-testid="opcao-texto-livre"
              >
                <FileText className="w-4 h-4 mb-1 text-slate-600" />
                <div className="text-xs font-semibold">Texto livre</div>
                <div className="text-[10px] text-slate-500">Sem vínculo de produto</div>
              </button>
            </div>
          </div>

          {/* Conteúdo da opção selecionada */}
          {opcao === 'vincular' && (
            <div className="space-y-2 p-3 rounded-lg bg-slate-50 border" data-testid="painel-vincular">
              <Label className="text-xs">Buscar produto cadastrado</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Digite parte do nome ou código..."
                  className="pl-7"
                  data-testid="input-search-produto"
                />
              </div>
              {searching && <div className="text-xs text-slate-500"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Buscando...</div>}
              {!searching && produtos.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded bg-white">
                  {produtos.map(p => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => {
                        setProdutoSelecionado(p);
                        // Auto-preenche a descrição final com o nome do produto vinculado
                        setDescricaoFinal(p.nome || '');
                      }}
                      className={`w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 border-b ${produtoSelecionado?.id === p.id ? 'bg-blue-100' : ''}`}
                      data-testid={`produto-result-${p.id}`}
                    >
                      <div className="font-medium">{p.nome}</div>
                      <div className="text-[10px] text-slate-500">
                        {p.sku && `Cód: ${p.sku} · `}{p.marca || ''} {p.modelo || ''} · {p.unidade}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {!searching && searchTerm.length >= 2 && produtos.length === 0 && (
                <div className="text-xs text-slate-400 italic">Nenhum produto encontrado para &quot;{searchTerm}&quot;</div>
              )}
              {produtoSelecionado && (
                <div className="text-xs p-2 bg-blue-100 border border-blue-200 rounded">
                  <span className="font-medium">Selecionado:</span> {produtoSelecionado.nome}
                </div>
              )}
            </div>
          )}

          {opcao === 'cadastrar' && (
            <div className="space-y-2 p-3 rounded-lg bg-emerald-50/30 border border-emerald-100" data-testid="painel-cadastrar">
              <Label className="text-xs font-semibold text-emerald-700">Cadastrar novo produto</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="md:col-span-2">
                  <Label className="text-[11px]">Nome <span className="text-red-500">*</span></Label>
                  <Input
                    value={novoProduto.nome}
                    onChange={e => setNovoProduto(p => ({ ...p, nome: e.target.value }))}
                    placeholder="Nome do produto"
                    data-testid="novo-prod-nome"
                  />
                </div>
                <div>
                  <Label className="text-[11px]">Código / SKU</Label>
                  <Input
                    value={novoProduto.sku}
                    onChange={e => setNovoProduto(p => ({ ...p, sku: e.target.value }))}
                    placeholder="(opcional — auto-gerado)"
                    data-testid="novo-prod-sku"
                  />
                </div>
                <div>
                  <Label className="text-[11px]">Unidade</Label>
                  <Select
                    value={novoProduto.unidade}
                    onValueChange={v => setNovoProduto(p => ({ ...p, unidade: v }))}
                  >
                    <SelectTrigger data-testid="novo-prod-unidade"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['UN', 'PC', 'KG', 'G', 'M', 'M2', 'M3', 'L', 'ML', 'CX', 'PCT', 'JG'].map(u => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px]">Categoria</Label>
                  <Select
                    value={novoProduto.categoria}
                    onValueChange={v => setNovoProduto(p => ({ ...p, categoria: v }))}
                  >
                    <SelectTrigger data-testid="novo-prod-categoria"><SelectValue placeholder="(opcional)" /></SelectTrigger>
                    <SelectContent>
                      {categoriasLookup.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px]">Marca</Label>
                  <Input
                    value={novoProduto.marca}
                    onChange={e => setNovoProduto(p => ({ ...p, marca: e.target.value }))}
                    placeholder="(opcional)"
                    data-testid="novo-prod-marca"
                  />
                </div>
              </div>
              <div className="text-[10px] text-slate-500 italic">
                Produto será criado com status &quot;cadastro_incompleto&quot; — você pode complementar em /produtos depois.
              </div>
            </div>
          )}

          {opcao === 'texto_livre' && (
            <div className="p-3 rounded-lg bg-slate-100 border" data-testid="painel-texto-livre">
              <div className="text-xs text-slate-700">
                O item da cotação será atualizado apenas com a nova descrição.
                <strong> Nenhum produto será criado nem vinculado.</strong>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Útil para compras pontuais que você não quer registrar no cadastro de produtos.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={loading} data-testid="btn-cancelar-alt">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmar}
            disabled={loading || !descricaoFinal?.trim() || (opcao === 'vincular' && !produtoSelecionado) || (opcao === 'cadastrar' && !novoProduto.nome?.trim())}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="btn-confirmar-alt"
          >
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            Adotar sugestão
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AvaliarAlternativaDialog;
